import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import { EmptyState, SectionCard, currency, number } from './ui';
import { CHART_COLORS, TRUE_COLORS } from '../theme';
import { clientInitials } from './Clients';
import { CardLinkButtons, useBusinessName } from './CardLink';

type Reward = { id: number; visit: number; title: string; expires_at: string; source: string };
type Member = {
  client: number; name: string; lastname: string; phone: string; visits: number;
  first_visit_day: string | null; last_visit_day: string | null; days_away: number | null;
  state: 'completed' | 'new' | 'away' | 'active'; rewards: Reward[]; rewards_used: number;
  next_unlock: { visit: number; title: string } | null; balance: number; invited: number; card_path: string | null;
  phone_e164?: string; marketing_opt_in?: boolean;
};
/** api/loyalty/customers/: every customer in the program and how far each one got. */
type Members = {
  program: { name: string; total_visits: number } | null; customers: Member[];
  reached: { visit: number; title: string; customers: number }[]; completed: number; away_days: number;
};
type Filter = 'all' | 'rewards' | 'away' | 'completed';

const fullName = (m: Member) => [m.name, m.lastname].filter(Boolean).join(' ') || 'Cliente';
const daysAgo = (days: number | null) =>
  days == null ? '—' : days === 0 ? 'Hoy' : days === 1 ? 'Ayer' : `Hace ${number(days)} días`;

const SearchToolbar = () => (
  <Box sx={{ px: 2, pt: 2, pb: 1 }}>
    <GridToolbarQuickFilter placeholder="Buscar por nombre o teléfono…" debounceMs={200}
      sx={{ width: { xs: '100%', sm: 360 }, '& .MuiInputBase-root': { borderRadius: 2 } }} />
  </Box>
);

/** Visit 3 of 10: a bar per visit of the route, the ones done filled. */
const Progress = ({ visits, total }: { visits: number; total: number }) => (
  <Stack sx={{ justifyContent: 'center', height: '100%', minWidth: 0 }} spacing={0.75}>
    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.2 }}>
      {total ? `Visita ${number(visits)} de ${number(total)}` : `${number(visits)} visitas`}
    </Typography>
    {total > 0 && (
      <Stack direction="row" spacing={0.375} sx={{ maxWidth: 160 }}>
        {Array.from({ length: total }, (_, i) => (
          <Box key={i} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: i < visits ? (visits >= total ? TRUE_COLORS.success : TRUE_COLORS.primary) : 'rgba(20,37,44,0.10)' }} />
        ))}
      </Stack>
    )}
  </Stack>
);

/** How many customers reached each visit of the route. */
const Route = ({ reached, members }: { reached: Members['reached']; members: number }) => (
  <Box sx={{ overflowX: 'auto', pb: 1 }}>
    <Stack direction="row" sx={{ minWidth: Math.max(reached.length * 108, 320) }}>
      {reached.map((step, i) => {
        const share = members ? step.customers / members : 0;
        return (
          <Box key={step.visit} sx={{ flex: 1, minWidth: 100, position: 'relative', px: 0.5, textAlign: 'center' }}>
            {i > 0 && <Box sx={{ position: 'absolute', top: 17, left: 0, width: '50%', height: 2, bgcolor: TRUE_COLORS.borderSolid }} />}
            {i < reached.length - 1 && <Box sx={{ position: 'absolute', top: 17, right: 0, width: '50%', height: 2, bgcolor: TRUE_COLORS.borderSolid }} />}
            <Box sx={{
              position: 'relative', mx: 'auto', width: 36, height: 36, borderRadius: '50%', display: 'grid', placeItems: 'center',
              fontWeight: 700, fontSize: '0.875rem', color: step.customers ? '#fff' : TRUE_COLORS.secondary,
              bgcolor: step.customers ? CHART_COLORS[0] : TRUE_COLORS.lightBg, border: `2px solid ${step.customers ? CHART_COLORS[0] : TRUE_COLORS.borderSolid}`,
            }}>
              {step.visit}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', mt: 1 }}>{number(step.customers)}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{number(share * 100)}%</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              title={step.title}>
              {step.title || `Visita ${step.visit}`}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  </Box>
);

/** Lealtad › Clientes: where every customer is in the route, the rewards they can use, their saldo. */
const LoyaltyCustomers = () => {
  const axios = useAxios();
  const business = useBusinessName();
  const navigate = useNavigate();
  const [data, setData] = React.useState<Members | null>(null);
  const [failed, setFailed] = React.useState(false);
  const [filter, setFilter] = React.useState<Filter>('all');

  React.useEffect(() => {
    axios.get('api/loyalty/customers/').then(r => setData(r.data)).catch(() => setFailed(true));
  }, [axios]);

  const members = data?.customers ?? [];
  const total = data?.program?.total_visits ?? 0;
  const counts: Record<Filter, number> = {
    all: members.length,
    rewards: members.filter(m => m.rewards.length > 0).length,
    away: members.filter(m => m.state === 'away').length,
    completed: members.filter(m => m.state === 'completed').length,
  };
  const shown = members.filter(m => filter === 'all' || (filter === 'rewards' ? m.rewards.length > 0 : m.state === filter));

  const columns: GridColDef[] = [
    {
      field: 'fullName', headerName: 'Cliente', flex: 1.3, minWidth: 220,
      valueGetter: (_v, row: Member) => `${fullName(row)} ${row.phone}`,
      renderCell: ({ row }: { row: Member }) => (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', height: '100%', minWidth: 0 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700, bgcolor: 'rgba(111,137,148,0.18)', color: TRUE_COLORS.primary }}>
            {clientInitials(row)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.3 }}>{fullName(row)}</Typography>
            <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{row.phone || 'Sin teléfono'}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'visits', headerName: 'Avance', type: 'number', align: 'left', headerAlign: 'left', flex: 0.9, minWidth: 170,
      renderCell: ({ row }: { row: Member }) => <Progress visits={row.visits} total={total} />,
    },
    {
      field: 'rewards', headerName: 'Por usar', flex: 1.2, minWidth: 190, sortable: true,
      valueGetter: (_v, row: Member) => row.rewards.length,
      renderCell: ({ row }: { row: Member }) => (row.rewards.length === 0 ? <Typography sx={{ color: 'text.secondary' }}>—</Typography> : (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', height: '100%', minWidth: 0 }}>
          {/* The one that expires first, and how many more (all of them in the tooltip). */}
          <Tooltip title={row.rewards.map(r => `${r.title} · vence el ${dayjs(r.expires_at).format('D MMM')}`).join(' · ')}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
              <Chip size="small" label={row.rewards[0].title} sx={{ bgcolor: 'rgba(194,168,120,0.18)', color: '#7a5d1e', fontWeight: 600, maxWidth: 170 }} />
              {row.rewards.length > 1 && <Chip size="small" label={`+${row.rewards.length - 1}`} sx={{ bgcolor: 'rgba(20,37,44,0.06)', fontWeight: 600 }} />}
            </Stack>
          </Tooltip>
        </Stack>
      )),
    },
    {
      field: 'next_unlock', headerName: 'Próxima recompensa', flex: 1.1, minWidth: 190,
      valueGetter: (_v, row: Member) => row.next_unlock?.title ?? '',
      renderCell: ({ row }: { row: Member }) => (
        <Stack sx={{ justifyContent: 'center', height: '100%', minWidth: 0 }}>
          {row.state === 'completed'
            ? <Typography sx={{ fontSize: '0.8125rem', color: TRUE_COLORS.success, fontWeight: 600 }}>Completó la ruta</Typography>
            : row.next_unlock
              ? <>
                  <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{row.next_unlock.title}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Para la visita {row.next_unlock.visit}</Typography>
                </>
              : <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>Sigue sumando visitas</Typography>}
        </Stack>
      ),
    },
    {
      field: 'days_away', headerName: 'Última visita', type: 'number', align: 'left', headerAlign: 'left', flex: 0.8, minWidth: 140,
      renderCell: ({ row }: { row: Member }) => (
        <Stack sx={{ justifyContent: 'center', height: '100%' }}>
          <Typography sx={{ fontSize: '0.8125rem' }}>{row.visits ? daysAgo(row.days_away) : 'Aún no viene'}</Typography>
          {row.state === 'away' && <Typography sx={{ fontSize: '0.75rem', color: TRUE_COLORS.warning, fontWeight: 600 }}>Sin venir hace un mes</Typography>}
          {row.state === 'new' && <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Invitado{row.rewards.length ? ', con su regalo' : ''}</Typography>}
        </Stack>
      ),
    },
    {
      field: 'balance', headerName: 'Saldo', type: 'number', flex: 0.6, minWidth: 100,
      valueFormatter: (v: number) => (v ? currency(v) : '—'),
    },
    {
      field: 'card', headerName: 'Tarjeta', width: 124, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: ({ row }: { row: Member }) => <CardLinkButtons holder={row} business={business} />,
    },
  ];

  if (failed) return <SectionCard><EmptyState compact title="No se pudo cargar el avance de tus clientes" /></SectionCard>;

  return (
    <Stack spacing={3}>
      {total > 0 && members.length > 0 && (
        <SectionCard title="Avance en la ruta" subtitle={`Cuántos clientes llegaron a cada visita · ${number(data?.completed ?? 0)} completaron la ruta`}>
          <Route reached={data?.reached ?? []} members={members.length} />
        </SectionCard>
      )}
      <SectionCard title="Clientes en el programa" subtitle={`${number(members.length)} clientes · ordenados por avance`} noPadding>
        <Box sx={{ px: 2, overflowX: 'auto' }}>
          <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}
            sx={{ '& .MuiToggleButton-root': { px: 1.5, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' } }}>
            <ToggleButton value="all">Todos ({number(counts.all)})</ToggleButton>
            <ToggleButton value="rewards">Con recompensa ({number(counts.rewards)})</ToggleButton>
            <ToggleButton value="away">Sin venir en {number(data?.away_days ?? 30)} días ({number(counts.away)})</ToggleButton>
            <ToggleButton value="completed">Completaron ({number(counts.completed)})</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <DataGrid
          rows={shown}
          getRowId={(row: Member) => row.client}
          columns={columns}
          loading={!data}
          autoHeight
          rowHeight={64}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50, 100]}
          onRowClick={params => navigate(`/admin/clients/${params.row.client}`)}
          slots={{
            toolbar: SearchToolbar,
            noRowsOverlay: () => (
              <EmptyState compact title={filter === 'all' ? 'Aún no hay clientes en el programa' : 'Nadie en este filtro'}
                description={filter === 'all' ? 'Cuando un cliente registrado compre, aquí verás su avance.' : undefined} />
            ),
          }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '--DataGrid-overlayHeight': '220px' }}
          data-testid="loyalty-customers"
        />
      </SectionCard>
    </Stack>
  );
};

export default LoyaltyCustomers;
